import type {
    ComposerAttributeOptionNode,
    ComposerOptionActionNode
} from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';

export interface ComposerSharedOptionAction {
    key: string;
    representative: ComposerOptionActionNode;
    actions: ComposerOptionActionNode[];
    hasMixedValues: boolean;
}

const normalizeSelector = (selector: string) =>
    selector
        .split(';')
        .map((value) => value.trim())
        .filter(Boolean)
        .sort((left, right) => left.localeCompare(right))
        .join(';');

/**
 * The value is intentionally excluded: it is the part users most often want to
 * replace across options (for example, the colour assigned to ELASTICBAND).
 */
export const getComposerSharedActionKey = (action: ComposerOptionActionNode): string =>
    [action.type, action.sceneElementType, normalizeSelector(action.selector || ''), action.property?.trim() || ''].join('|');

/**
 * Returns only actions that can be matched without guessing: every selected
 * option must contain exactly one action with the same type, target and property.
 */
export const getComposerSharedOptionActions = (options: ComposerAttributeOptionNode[]): ComposerSharedOptionAction[] => {
    if (options.length < 2) return [];

    const actionsByOption = options.map((option) => {
        const actionsByKey = new Map<string, ComposerOptionActionNode[]>();
        option.actions.forEach((action) => {
            const key = getComposerSharedActionKey(action);
            actionsByKey.set(key, [...(actionsByKey.get(key) || []), action]);
        });
        return actionsByKey;
    });

    const firstOptionKeys = Array.from(new Set(options[0].actions.map(getComposerSharedActionKey)));
    return firstOptionKeys.flatMap((key) => {
        const matches = actionsByOption.map((actionsByKey) => actionsByKey.get(key) || []);
        if (matches.some((actions) => actions.length !== 1)) return [];

        const actions = matches.map(([action]) => action);
        return [
            {
                key,
                representative: actions[0],
                actions,
                hasMixedValues: new Set(actions.map((action) => action.value)).size > 1
            }
        ];
    });
};

/**
 * Applies only fields exposed by the action editor. Per-option identity and
 * persistence metadata must stay untouched so saving cannot move or duplicate
 * actions between options.
 */
export const applyComposerSharedActionEdit = (actions: ComposerOptionActionNode[], editedAction: ComposerOptionActionNode): ComposerOptionActionNode[] =>
    actions.map((action) => ({
        ...action,
        type: editedAction.type,
        sceneElementType: editedAction.sceneElementType,
        selector: editedAction.selector,
        property: editedAction.property,
        value: editedAction.value
    }));
